import React, { useState, useEffect } from 'react';
import { alertsAPI } from '../services/api';
import './AIAnalysis.css';

const AIAnalysis = ({ alertId, initialAnalysis }) => {
  const [analysis, setAnalysis] = useState(initialAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Always use latest initialAnalysis when parent refreshes the alert
    if (initialAnalysis && initialAnalysis.status === 'completed') {
      setAnalysis(initialAnalysis);
    } else if (!loading) {
      loadAnalysis();
    }
  }, [alertId, initialAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAnalysis = async () => {
    try {
      const response = await alertsAPI.getAnalysis(alertId);
      const a = response.data.analysis;
      // Treat empty/cache-only results with no real content as null
      const hasContent = a && a.status === 'completed' && (
        a.root_cause?.summary ||
        a.remediation?.immediate_actions?.some(x => x.action)
      );
      setAnalysis(hasContent ? a : null);
    } catch (err) {
      if (err.response?.status === 404) {
        setAnalysis(null);
      } else {
        console.error('Failed to load analysis:', err);
      }
    }
  };

  const triggerAnalysis = async (force = false) => {
    setLoading(true);
    setError(null);
    setAnalysis(null);
    try {
      await alertsAPI.triggerAnalysis(alertId, force);
      pollAnalysisStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to trigger analysis');
      setLoading(false);
    }
  };

  const pollAnalysisStatus = async () => {
    const maxAttempts = 120; // 2 minutes — Claude takes 20-30s + queue wait
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await alertsAPI.getAnalysisStatus(alertId);
        const status = response.data.status;

        if (status === 'completed') {
          await loadAnalysis();
          setLoading(false);
        } else if (status === 'failed') {
          setError('AI analysis failed. Click Re-analyze to retry.');
          setLoading(false);
        } else if (attempts < maxAttempts) {
          attempts++;
          // Poll every 2s for first 30s, then every 3s to reduce load
          setTimeout(poll, attempts < 15 ? 2000 : 3000);
        } else {
          setError('Analysis is taking longer than expected. It may still complete — refresh the page or click Re-analyze.');
          setLoading(false);
        }
      } catch (err) {
        setError('Failed to check analysis status');
        setLoading(false);
      }
    };

    poll();
  };

  const renderOverview = () => {
    if (!analysis) return null;

    const rootCause = analysis.root_cause || {};
    const impact = analysis.impact || {};

    return (
      <div className="ai-overview">
        <div className="ai-section">
          <h4>Root Cause</h4>
          <div className="root-cause-summary">
            {rootCause.summary || 'No summary available'}
          </div>
          {rootCause.confidence_score && (
            <div className="confidence-indicator">
              <span className="confidence-label">Confidence:</span>
              <div className="confidence-bar">
                <div
                  className="confidence-fill"
                  style={{ width: `${rootCause.confidence_score * 100}%` }}
                />
              </div>
              <span className="confidence-value">
                {(rootCause.confidence_score * 100).toFixed(0)}%
              </span>
            </div>
          )}
        </div>

        <div className="ai-section">
          <h4>Impact Assessment</h4>
          <div className="impact-grid">
            <div className="impact-item">
              <span className="impact-label">Severity:</span>
              <span className={`impact-badge severity-${impact.severity_level}`}>
                {impact.severity_level}
              </span>
            </div>
            <div className="impact-item">
              <span className="impact-label">Scope:</span>
              <span className="impact-value">{impact.scope}</span>
            </div>
            {impact.user_impact && (
              <div className="impact-item">
                <span className="impact-label">User Impact:</span>
                <span className="impact-value">
                  {typeof impact.user_impact === 'string'
                    ? impact.user_impact
                    : `~${impact.user_impact.estimated_affected_users} users`}
                </span>
              </div>
            )}
            {impact.sla_breach_risk && (
              <div className="impact-item">
                <span className="impact-label">SLA Risk:</span>
                <span className={`impact-badge severity-${impact.sla_breach_risk}`}>{impact.sla_breach_risk}</span>
              </div>
            )}
          </div>
        </div>

        <div className="ai-metadata">
          <span>Analyzed by SarvKube AI</span>
          <span>{analysis.duration_ms}ms</span>
          <span>{analysis.model?.total_tokens_used} tokens</span>
        </div>
      </div>
    );
  };

  const renderRootCause = () => {
    if (!analysis?.root_cause) return null;

    const rc = analysis.root_cause;

    return (
      <div className="ai-root-cause">
        <div className="rc-section">
          <h4>Summary</h4>
          <p>{rc.summary}</p>
        </div>

        {rc.details && (
          <div className="rc-section">
            <h4>Details</h4>
            <p>{rc.details}</p>
          </div>
        )}

        {rc.technical_explanation && (
          <div className="rc-section">
            <h4>Technical Explanation</h4>
            <p>{rc.technical_explanation}</p>
          </div>
        )}

        {rc.affected_components && rc.affected_components.length > 0 && (
          <div className="rc-section">
            <h4>Affected Components</h4>
            <ul className="components-list">
              {rc.affected_components.map((comp, idx) => (
                <li key={idx}>{comp}</li>
              ))}
            </ul>
          </div>
        )}

        {rc.confidence_reasoning && (
          <div className="rc-section">
            <h4>Confidence Reasoning</h4>
            <p>{rc.confidence_reasoning}</p>
          </div>
        )}
      </div>
    );
  };

  const renderRemediation = () => {
    if (!analysis?.remediation) return null;

    const rem = analysis.remediation;

    return (
      <div className="ai-remediation">
        {rem.recommended_approach && (
          <div className="recommended-approach">
            <span className="approach-label">Recommended Approach:</span>
            <span className={`approach-badge approach-${rem.recommended_approach}`}>
              {rem.recommended_approach}
            </span>
          </div>
        )}

        {rem.immediate_actions && rem.immediate_actions.length > 0 && (
          <div className="rem-section">
            <h4>Immediate Actions</h4>
            {rem.immediate_actions.map((action, idx) => (
              <div key={idx} className="action-card">
                <div className="action-header">
                  <span className="action-priority">Priority {action.priority}</span>
                  <span className={`action-risk risk-${action.risk_level}`}>
                    {action.risk_level} risk
                  </span>
                </div>
                <div className="action-content">
                  <p><strong>{action.action}</strong></p>
                  {action.command && (
                    <code className="action-command">{action.command}</code>
                  )}
                  {action.expected_outcome && (
                    <p className="action-outcome">
                      <em>Expected: {action.expected_outcome}</em>
                    </p>
                  )}
                  {action.estimated_time && (
                    <span className="action-time">{action.estimated_time}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {rem.short_term_actions && rem.short_term_actions.length > 0 && (
          <div className="rem-section">
            <h4>Short-term Actions</h4>
            {rem.short_term_actions.map((action, idx) => (
              <div key={idx} className="action-card">
                {typeof action === 'string'
                  ? <p>{action}</p>
                  : <>
                      <p><strong>{action.action}</strong></p>
                      {action.steps && <ol className="action-steps">{action.steps.map((s,i)=><li key={i}>{s}</li>)}</ol>}
                    </>
                }
              </div>
            ))}
          </div>
        )}

        {rem.long_term_actions && rem.long_term_actions.length > 0 && (
          <div className="rem-section">
            <h4>Long-term Actions</h4>
            {rem.long_term_actions.map((action, idx) => (
              <div key={idx} className="action-card">
                {typeof action === 'string'
                  ? <p>{action}</p>
                  : <>
                      <p><strong>{action.action}</strong></p>
                      {action.steps && <ol className="action-steps">{action.steps.map((s,i)=><li key={i}>{s}</li>)}</ol>}
                      {action.owner && <p className="action-owner">Owner: {action.owner}</p>}
                    </>
                }
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPrevention = () => {
    if (!analysis?.prevention) return null;

    const prev = analysis.prevention;

    return (
      <div className="ai-prevention">
        {prev.configuration_changes && prev.configuration_changes.length > 0 && (
          <div className="prev-section">
            <h4>Configuration Changes</h4>
            {prev.configuration_changes.map((change, idx) => (
              <div key={idx} className="prev-card">
                {typeof change === 'string'
                  ? <p>{change}</p>
                  : <>
                      {change.component && <h5>{change.component}</h5>}
                      <p><strong>Change:</strong> {change.change}</p>
                      {change.details && <p><strong>Details:</strong> {change.details}</p>}
                      {change.benefit && <p className="prev-benefit"><em>{change.benefit}</em></p>}
                    </>
                }
              </div>
            ))}
          </div>
        )}

        {prev.monitoring_improvements && prev.monitoring_improvements.length > 0 && (
          <div className="prev-section">
            <h4>Monitoring Improvements</h4>
            {prev.monitoring_improvements.map((imp, idx) => (
              <div key={idx} className="prev-card">
                {typeof imp === 'string'
                  ? <p>{imp}</p>
                  : <>
                      {imp.metric && <p><strong>Metric:</strong> {imp.metric}</p>}
                      {imp.threshold && <p><strong>Threshold:</strong> {imp.threshold}</p>}
                      {imp.alert_before && <p><strong>Alert Before:</strong> {imp.alert_before}</p>}
                      {imp.implementation && <p className="prev-implementation">{imp.implementation}</p>}
                    </>
                }
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!analysis && !loading) {
    return (
      <div className="ai-analysis-container">
        <div className="ai-empty">
          <p>No AI analysis available for this alert</p>
          <button
            className="btn-trigger-analysis"
            onClick={() => triggerAnalysis(false)}
            disabled={loading}
          >
            Analyze with AI
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ai-analysis-container">
        <div className="ai-loading">
          <div className="spinner"></div>
          <p>AI is analyzing the alert...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-analysis-container">
        <div className="ai-error">
          <p>{error}</p>
          <button
            className="btn-trigger-analysis"
            onClick={() => triggerAnalysis(true)}
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-analysis-container">
      <div className="ai-header">
        <h3>AI Analysis</h3>
        <button
          className="btn-reanalyze"
          onClick={() => triggerAnalysis(true)}
          disabled={loading}
        >
          Re-analyze
        </button>
      </div>

      <div className="ai-tabs">
        <button
          className={`ai-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`ai-tab ${activeTab === 'rootcause' ? 'active' : ''}`}
          onClick={() => setActiveTab('rootcause')}
        >
          Root Cause
        </button>
        <button
          className={`ai-tab ${activeTab === 'remediation' ? 'active' : ''}`}
          onClick={() => setActiveTab('remediation')}
        >
          Remediation
        </button>
        <button
          className={`ai-tab ${activeTab === 'prevention' ? 'active' : ''}`}
          onClick={() => setActiveTab('prevention')}
        >
          Prevention
        </button>
      </div>

      <div className="ai-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'rootcause' && renderRootCause()}
        {activeTab === 'remediation' && renderRemediation()}
        {activeTab === 'prevention' && renderPrevention()}
      </div>
    </div>
  );
};

export default AIAnalysis;
